import { useMemo } from "react";

import { EmptystateVariablesColored } from "components/icons/generated";
import PageWrapper from "components/PageWrapper";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import useTypedContext from "hooks/useTypedContext";
import { ConfigElementWithSource, StackContextAttachment } from "types/generated";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Tooltip from "ds/components/Tooltip";
import Counter from "ds/components/Counter";
import CollapsibleSection from "ds/components/CollapsibleSection";

import { showEnvVariableFormDrawer } from "../components/EnvVariablesDrawer/EnvVariablesFormDrawer";
import { showMountedFilesFormDrawer } from "../components/MountedFileDrawer/MountedFilesFormDrawer";
import EnvVariableActionsCell from "../components/Table/Cells/EnvVariableActionsCell";
import MountedFilesActionsCell from "../components/Table/Cells/MountedFilesActionsCell";
import EnvironmentTable from "../components/Table/EnvironmentTable";
import ContextsRow from "../components/Table/Rows/ContextsRow";
import FilesRow from "../components/Table/Rows/FilesRow";
import VariablesRow from "../components/Table/Rows/VariablesRow";
import { ContextColumnId, CONTEXTS_COLUMNS } from "../constants/contextTable";
import { ENV_VARS_COLUMNS, EnvVariablesColumnId } from "../constants/envVariableTable";
import { CUSTOM_END_COLUMN_CONFIG } from "../constants/globalTable";
import { FilesColumnId, MOUNTED_FILES_COLUMNS } from "../constants/mountedFilesTable";
import { SPACELIFT_VARS_COLUMNS } from "../constants/spaceliftVarsTable";
import { EnvironmentContext } from "../EnvironmentContext";

type EnvVariable = ConfigElementWithSource & { id: string };

type EnvironmentPageProps = {
  localVariables: EnvVariable[];
  mountedFiles: EnvVariable[];
  spaceliftVariables: EnvVariable[];
  attachedContexts: StackContextAttachment[];
};

const EnvironmentPage = ({
  localVariables,
  mountedFiles,
  spaceliftVariables,
  attachedContexts,
}: EnvironmentPageProps) => {
  const { canCreateAndEditVariables, canDeleteVariables, onCreate } =
    useTypedContext(EnvironmentContext);

  const isAddVariableVisible =
    canCreateAndEditVariables.result || !!canCreateAndEditVariables.message;

  const isDeleteVariableVisible = canDeleteVariables.result || !!canDeleteVariables.message;
  const existingNames = useMemo(() => localVariables.map((v) => v.id), [localVariables]);

  return (
    <>
      <PageInfoHeader title="Environment" />
      <PageWrapper margin="0" padding="0" gap="x-large">
        <CollapsibleSection
          ariaLevel={3}
          title="Environment variables"
          initialIsCollapsed={false}
          titleSuffix={<Counter count={localVariables.length} />}
          ariaLabel="Environment variables"
          fullbleed
          headerSuffix={
            isAddVariableVisible ? (
              <Tooltip
                textAlign="center"
                active={!!canCreateAndEditVariables.message}
                on={(tooltipProps) => (
                  <Button
                    {...tooltipProps}
                    variant="secondary"
                    disabled={!canCreateAndEditVariables.result}
                    onPress={() =>
                      showEnvVariableFormDrawer({
                        onSubmit: onCreate,
                        existingNames,
                      })
                    }
                  >
                    Add variable
                  </Button>
                )}
              >
                {canCreateAndEditVariables.message}
              </Tooltip>
            ) : undefined
          }
        >
          <EnvironmentTable
            columnsConfig={ENV_VARS_COLUMNS}
            endCustomColumns={
              isAddVariableVisible || isDeleteVariableVisible ? CUSTOM_END_COLUMN_CONFIG : null
            }
            emptyTable={
              <EmptyState
                padding="x-large 0"
                icon={EmptystateVariablesColored}
                title="You do not have any variables yet"
                caption="Environment variables are represented as a key-value mapping available to all processes running in a given environment. Click on the button above to create a new variable."
              />
            }
            items={localVariables}
            rowRenderer={(item, key) => (
              <VariablesRow key={key} config={item} actionsComponent={EnvVariableActionsCell} />
            )}
            rowHeaderId={EnvVariablesColumnId.CREATED_AT}
            tableId="stack-env-variables"
          />
        </CollapsibleSection>
        <CollapsibleSection
          ariaLevel={3}
          title="Mounted files"
          titleSuffix={<Counter count={mountedFiles.length} />}
          ariaLabel="Mounted files"
          fullbleed
          headerSuffix={
            isAddVariableVisible ? (
              <Tooltip
                textAlign="center"
                active={!!canCreateAndEditVariables.message}
                on={(tooltipProps) => (
                  <Button
                    {...tooltipProps}
                    variant="secondary"
                    disabled={!canCreateAndEditVariables.result}
                    onPress={() =>
                      showMountedFilesFormDrawer({
                        onSubmit: onCreate,
                      })
                    }
                  >
                    Add file
                  </Button>
                )}
              >
                {canCreateAndEditVariables.message}
              </Tooltip>
            ) : undefined
          }
        >
          <EnvironmentTable
            columnsConfig={MOUNTED_FILES_COLUMNS}
            endCustomColumns={
              isAddVariableVisible || isDeleteVariableVisible ? CUSTOM_END_COLUMN_CONFIG : null
            }
            emptyTable={
              <EmptyState
                padding="x-large 0"
                icon={EmptystateVariablesColored}
                title="There are no mounted files for this environment"
                caption="Mounted files are represented as a key-value mapping available to all processes running in a given environment."
              />
            }
            items={mountedFiles}
            rowRenderer={(item, key) => (
              <FilesRow key={key} config={item} actionsComponent={MountedFilesActionsCell} />
            )}
            rowHeaderId={FilesColumnId.CREATED_AT}
            tableId="stack-mounted-files"
          />
        </CollapsibleSection>
        <CollapsibleSection
          ariaLevel={3}
          title="Spacelift environment"
          titleSuffix={<Counter count={spaceliftVariables.length} />}
          ariaLabel="Spacelift environment"
          fullbleed
        >
          <EnvironmentTable
            columnsConfig={SPACELIFT_VARS_COLUMNS}
            emptyTable={
              <EmptyState
                padding="x-large 0"
                icon={EmptystateVariablesColored}
                title="There are no Spacelift variables for this environment"
                caption="Environment variables are represented as a key-value mapping available to all processes running in a given environment."
              />
            }
            items={spaceliftVariables}
            rowRenderer={(item, key) => (
              <VariablesRow key={key} config={item} actionsComponent={() => undefined} />
            )}
            rowHeaderId={EnvVariablesColumnId.NAME}
            tableId="stack-spacelift-env-variables"
          />
        </CollapsibleSection>

        {attachedContexts.length ? (
          <CollapsibleSection
            ariaLevel={3}
            title="Attached contexts"
            titleSuffix={<Counter count={attachedContexts.length} />}
            fullbleed
            ariaLabel="Attached contexts"
          >
            <EnvironmentTable
              columnsConfig={CONTEXTS_COLUMNS}
              items={attachedContexts}
              rowRenderer={(item, key) => <ContextsRow key={key} context={item} />}
              rowHeaderId={ContextColumnId.NAME}
              tableId="stack-attached-contexts"
            />
          </CollapsibleSection>
        ) : null}
      </PageWrapper>
    </>
  );
};

export default EnvironmentPage;
